import { mulberry32, seedFrom } from "./random.js";
import { COLORS, SHAPES, packToken } from "./sequence.js";
/**
 * Board shapes, chosen so a card never falls below the touch floor.
 *
 * Three columns is the practical maximum on a 360 px phone: at four, a card with its gaps lands
 * around 76 px, which is the floor rather than a comfortable target — and a mis-tap here does not
 * just cost a point, it reveals a card the child did not choose to reveal.
 *
 * That ceiling is why levels 6 and 8 repeat the board below them and change the CARDS instead.
 * Four columns would have been the easy way to keep adding pairs, and it would have bought levels
 * by making the game about aim.
 */
function shapeFor(difficulty) {
    const plain = { colorOnly: false };
    switch (difficulty) {
        case 1:
            return { ...plain, pairs: 2, cols: 2, rows: 2 };
        case 2:
            return { ...plain, pairs: 3, cols: 3, rows: 2 };
        case 3:
            return { ...plain, pairs: 4, cols: 2, rows: 4 };
        case 4:
            return { ...plain, pairs: 5, cols: 2, rows: 5 };
        case 5:
            return { ...plain, pairs: 6, cols: 3, rows: 4 };
        case 6:
            return { pairs: 6, cols: 3, rows: 4, colorOnly: true };
        case 7:
            return { ...plain, pairs: 9, cols: 3, rows: 6 };
        default:
            return { pairs: 9, cols: 3, rows: 6, colorOnly: true };
    }
}
export const PAIRS_MIN_DIFFICULTY = 1;
export const PAIRS_MAX_DIFFICULTY = 8;
export function generatePairs(task, formId, trialIndex, difficulty) {
    if (difficulty < PAIRS_MIN_DIFFICULTY || difficulty > PAIRS_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { pairs, cols, rows, colorOnly } = shapeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    // Distinct tokens, so two different pairs can never look alike. A repeat would make a "wrong"
    // match indistinguishable from a right one to the child AND to the scoring.
    const chosen = new Set();
    let guard = 0;
    if (colorOnly) {
        /**
         * One shape, every colour, then more shapes only if nine pairs demand it.
         *
         * Four colours cannot make nine distinct pairs, so a nine-pair colour-only board is drawn as
         * few shapes as possible rather than one: three shapes across four colours is twelve tokens,
         * and every pair still shares a shape with two others. Refusing to draw at all would mean a
         * legal level that throws.
         */
        const shapesNeeded = Math.ceil(pairs / COLORS);
        const shapes = Array.from({ length: shapesNeeded }, () => Math.floor(rand() * SHAPES));
        while (chosen.size < pairs && guard++ < 500) {
            const shape = shapes[Math.floor(rand() * shapes.length)];
            chosen.add(packToken(shape, Math.floor(rand() * COLORS)));
        }
    }
    else {
        while (chosen.size < pairs && guard++ < 500) {
            chosen.add(packToken(Math.floor(rand() * SHAPES), Math.floor(rand() * COLORS)));
        }
    }
    if (chosen.size < pairs) {
        throw new Error(`could not draw ${pairs} distinct tokens for ${task.id}`);
    }
    const cards = [...chosen, ...chosen];
    // Fisher-Yates, from the seeded source, so a board is reproducible years later from the record.
    for (let i = cards.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    return { cols, rows, cards, pairs };
}
/**
 * The fewest card turns the board can be cleared in.
 *
 * Two per pair: a child who remembers everything turns each card exactly once. It is the baseline
 * efficiency is measured against, and it is unreachable in practice for the first pair — you cannot
 * match a card you have never seen — which is why the budget below is a multiple rather than a
 * margin.
 */
export const minimumFlips = (board) => board.pairs * 2;
/**
 * Turns allowed before the round counts as unfinished.
 *
 * Three times the minimum. Generous, because the point is not to punish a child for looking: it
 * exists so difficulty can adapt at all. Without a ceiling every round ends in success, the step-up
 * rule fires forever, and a child is pushed to a board they cannot hold while the record says they
 * were doing fine.
 */
export const flipBudget = (board) => minimumFlips(board) * 3;
/** Cleared the board within the budget. */
export function scorePairsTrial(board, flips) {
    return flips <= flipBudget(board);
}
//# sourceMappingURL=pairs.js.map